import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const FAVORITES_SYNC_EVENT = "clickandgard:favorites-sync";

type FavoriteSyncDetail = {
  proId: string;
  favorite: boolean;
};

export const useFavorites = () => {
  const { user, role } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || role !== "parent") {
      setFavoriteIds(new Set());
      setLoading(false);
      return;
    }

    setLoading(true);
    supabase
      .from("favorites")
      .select("pro_id")
      .eq("user_id", user.id)
      .then(({ data }) => {
        setFavoriteIds(new Set((data ?? []).map((f) => f.pro_id)));
        setLoading(false);
      });
  }, [user, role]);

  // Plusieurs composants utilisent ce hook en même temps (navbar, cartes,
  // fiche profil). Cet événement garde tous les cœurs/compteurs synchronisés
  // immédiatement sans attendre un rechargement ou une nouvelle requête.
  useEffect(() => {
    if (!user || role !== "parent" || typeof window === "undefined") return;

    const handleSync = (event: Event) => {
      const detail = (event as CustomEvent<FavoriteSyncDetail>).detail;
      if (!detail?.proId) return;
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (detail.favorite) next.add(detail.proId);
        else next.delete(detail.proId);
        return next;
      });
    };

    window.addEventListener(FAVORITES_SYNC_EVENT, handleSync);
    return () => window.removeEventListener(FAVORITES_SYNC_EVENT, handleSync);
  }, [user, role]);

  const broadcastFavorite = useCallback((proId: string, favorite: boolean) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent<FavoriteSyncDetail>(FAVORITES_SYNC_EVENT, {
        detail: { proId, favorite },
      }),
    );
  }, []);

  const toggle = useCallback(
    async (proId: string) => {
      if (!user || role !== "parent") return false;

      const wasFavorite = favoriteIds.has(proId);
      const nextFavorite = !wasFavorite;

      // Mise à jour optimiste pour un cœur instantané sur mobile et desktop.
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (nextFavorite) next.add(proId);
        else next.delete(proId);
        return next;
      });
      broadcastFavorite(proId, nextFavorite);

      const { error } = nextFavorite
        ? await supabase.from("favorites").insert({ user_id: user.id, pro_id: proId })
        : await supabase.from("favorites").delete().eq("user_id", user.id).eq("pro_id", proId);

      if (error) {
        // Rollback de tous les composants si la persistance échoue.
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          if (wasFavorite) next.add(proId);
          else next.delete(proId);
          return next;
        });
        broadcastFavorite(proId, wasFavorite);
        throw error;
      }

      return nextFavorite;
    },
    [user, role, favoriteIds, broadcastFavorite],
  );

  const isFavorite = useCallback((proId: string) => favoriteIds.has(proId), [favoriteIds]);

  return { favoriteIds, isFavorite, toggle, loading };
};
